import { PaginationControls } from "@/domains/documents/components/molecules/PaginationControls";
import { SignaturePanelProvider } from "@/domains/documents/components/organisms/SignaturePanelProvider";
import { cn } from "@/lib/utils";
import { observer } from "mobx-react-lite";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import { TableBulkActions } from "./TableBulkActions";
import { TableIfcCompareDialog, TablePdfCompareDialog } from "./TableCompareDialogs";
import { TableChildrenDialog, TableDeleteDialog, TableViewerDialog } from "./TableDocumentDialogs";
import { TableHeader } from "./TableHeader";
import { TableEmpty, TableScrolling } from "./TableScrolling";
import {
  TableAddVersionsDialog,
  TableHistoryDialog,
  TableSignDialog,
  TableTeachDialog,
} from "./TableVersionDialogs";
export const TableLayout = observer(function TableLayout(props: DocumentTablePresentation) {
  const { onShare, store, signingEnabled, setSignTarget, setIsSignDialogOpen, activeMove, t } =
    props;
  return (
    <SignaturePanelProvider
      onShare={
        onShare && store
          ? (containerId) => {
              store.clearSelection();
              store.selectDocument(containerId, true);
              onShare();
            }
          : undefined
      }
      onSign={
        signingEnabled
          ? (container) => {
              setSignTarget({
                id: parseInt(container.id, 10),
                filename: container.filename,
                size: container.size ?? 0,
              });
              setIsSignDialogOpen(true);
            }
          : undefined
      }
    >
      <TableContainer {...props} />

      {/* Delete / Restore confirmation dialog */}
      <TableDeleteDialog {...props} />

      {/* Document preview dialog */}
      <TableViewerDialog {...props} />

      {/* PDF compare dialog */}
      <TablePdfCompareDialog {...props} />

      {/* WHA-3695: data-only IFC differences overview */}
      <TableIfcCompareDialog {...props} />

      {/* Child documents modal */}
      <TableChildrenDialog {...props} />

      {/* Teach Franklin modal for tag correction */}
      <TableTeachDialog {...props} />

      {/* Version history modal */}
      <TableHistoryDialog {...props} />

      {/* "Add new version" row action — stage and order uploads and existing
          documents as consecutive versions. */}
      <TableAddVersionsDialog {...props} />

      {/* Digital signing (Dokobit Documents Gateway) — opt-in, org-gated. */}
      <TableSignDialog {...props} />

      {/* The bulk bar and the live region come last, not first: hosts lay this
          component out in `space-y-*` stacks (ProjectDocumentView), and
          anything before the table — even these invisible elements — pushed it
          16px down from whatever sat above it. */}
      {/* Only show bulk actions when selection is enabled, store is available, and not hidden */}
      <TableBulkActions {...props} />
      <span className="sr-only" aria-live="polite">
        {activeMove
          ? t.documents.folder.draggingItems.replace(
              "{count}",
              String(activeMove.docIds.length + activeMove.folderIds.length)
            )
          : ""}
      </span>
    </SignaturePanelProvider>
  );
});

export const TableContainer = observer(function TableContainer(props: DocumentTablePresentation) {
  const {
    tableWrapperRef,
    className,
    fittedColumns,
    showLoadingOverlay,
    store,
    minWidth,
    showPaginationControls,
    paginationProps,
  } = props;
  return (
    <div
      ref={tableWrapperRef}
      className={cn("relative w-full overflow-x-auto", className)}
      style={
        fittedColumns.nameMinWidth === undefined
          ? undefined
          : ({
              "--document-name-min-width": `${fittedColumns.nameMinWidth}px`,
            } as React.CSSProperties)
      }
      aria-busy={showLoadingOverlay}
      onClick={(e) => {
        // Clear selection when clicking on empty space (not on a row)
        if (e.target === e.currentTarget && store) {
          store.clearSelection();
        }
      }}
    >
      <div className={minWidth}>
        {/* Sticky header - outside scroll container */}
        <TableHeader {...props} />

        {/* Empty state - rendered inside the table so header stays visible */}
        <TableEmpty {...props} />

        {/* Virtual scroll container, capped to the space left below it */}
        <TableScrolling {...props} />

        {/* Pagination - bottom right */}
        {showPaginationControls && paginationProps && (
          <div className="flex justify-end border-t px-4 py-2">
            <PaginationControls {...paginationProps} isLoading={showLoadingOverlay} />
          </div>
        )}
      </div>
    </div>
  );
});
